import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { enums } from "../../constants/enum";
import { orderUpdateSchema } from "../../schemas/order";
import useOrderFormService from "../../services/api/orderFormService";
import useOrderService from "../../services/api/orderService";
import currencyFormatter from "../../util/currencyFormatter";
import dateFormatter from "../../util/dateFormatter";

import {
  EditIcon,
  EyeIcon,
  FlagLineIcon,
  FlagSolidIcon,
} from "../../assets/icons";
import {
  Button,
  Chip,
  ListBox,
  Loading,
  Modal,
  PageTitle,
  Section,
} from "../../components";
import {
  Checkbox,
  EyeMeasurementInput,
  SelectInput,
  TextInput,
} from "../../components/forms";
import "./OrdersPage.scss";

function getOrderStatusChipColor(orderStatus) {
  switch (orderStatus) {
    case enums.ORDER_STATUSES.READY:
      return "info";
    case enums.ORDER_STATUSES.CONFIRMED:
      return "warning";
    case enums.ORDER_STATUSES.COMPLETED:
      return "success";
    case enums.ORDER_STATUSES.CANCELLED:
      return "danger";
    default:
      return "default";
  }
}

function OrderDetailsPage() {
  const { orderId } = useParams();
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);

  const { getOrderById, toggleOrderFlag, queryKey } = useOrderService();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: [queryKey, orderId],
    queryFn: () => getOrderById(orderId),
    enabled: !!orderId,
  });

  const toggleFlagMutation = useMutation({
    mutationFn: toggleOrderFlag,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success("Order flag updated");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to update flag");
    },
  });

  if (isLoading) {
    return <Loading variant="fullScreen" />;
  }

  if (error) {
    toast.error(error.message || enums.DEFAULT_ERROR_MESSAGE);
  }

  function getOrderCompletedDate() {
    if (data?.order.status === enums.ORDER_STATUSES.COMPLETED) {
      return data?.order.payments[data?.order.payments.length - 1].date;
    }
    return "";
  }
  function getRemainingAmount() {
    const discount = data?.order?.orderForm?.discount || 0;
    if (data?.order.status !== enums.ORDER_STATUSES.COMPLETED) {
      const totalAmount = data?.order.payments.reduce(
        (acc, payment) => acc + payment.amount,
        0,
      );
      return data?.order.totalAmount - totalAmount - discount;
    }
    return 0;
  }

  return (
    <>
      <div className="page order-details-page">
        <PageTitle text="Order Details" prev="/orders">
          <div className="action-list-wide">
            {data?.order.status === enums.ORDER_STATUSES.CONFIRMED ||
            data?.order.status === enums.ORDER_STATUSES.READY ? (
              <Button
                text="Update Order Status"
                icon={<EditIcon />}
                onClick={() => setShowStatusModal(true)}
              />
            ) : null}
            <Button
              text="View Prescription"
              icon={<EyeIcon />}
              onClick={() => setShowPrescriptionModal(true)}
            />
          </div>

          <div className="action-list">
            <ListBox
              options={[
                ...(data?.order.status === enums.ORDER_STATUSES.CONFIRMED ||
                data?.order.status === enums.ORDER_STATUSES.READY
                  ? [
                      {
                        text: "Update Order Status",
                        onClick: () => setShowStatusModal(true),
                      },
                    ]
                  : []),
                {
                  text: "View Prescription",
                  onClick: () => setShowPrescriptionModal(true),
                },
              ]}
            />
          </div>
        </PageTitle>

        <Section>
          <div className="data-sec">
            <div className="left-bx">
              <div className="d-row">
                <div>
                  <span>Flag</span>
                  <button
                    type="button"
                    className="flag-btn"
                    onClick={() => toggleFlagMutation.mutate(orderId)}
                    disabled={toggleFlagMutation.isPending}
                  >
                    {data?.order.isFlagged ? (
                      <FlagSolidIcon />
                    ) : (
                      <FlagLineIcon />
                    )}
                  </button>
                </div>

                <div>
                  <span>Order ID</span>
                  <h2>{data?.order.orderId}</h2>
                </div>
              </div>

              <div>
                <span>Order Type</span>
                <p>
                  {data?.order.orderForm?.campaign
                    ? enums.ORDER_TYPES.CAMPAIGN_ORDER
                    : enums.ORDER_TYPES.SHOP_ORDER}
                </p>
              </div>
              {data?.order.orderForm?.campaign ? (
                <div>
                  <span>Campaign Name</span>
                  <p>{data?.order.orderForm?.campaign?.name}</p>
                </div>
              ) : null}

              <div>
                <span>Order Date & Time</span>
                <p>
                  <small>Date:</small> {dateFormatter(data?.order.orderDate)}
                </p>
              </div>

              <div>
                <span>Order Amount</span>
                <p>
                  {currencyFormatter(
                    data?.order.totalAmount -
                      (data?.order.orderForm?.discount || 0),
                  )}{" "}
                  LKR
                </p>
              </div>

              <div>
                <span>Completed Date</span>
                <p>{dateFormatter(getOrderCompletedDate()) || "N/A"}</p>
              </div>
            </div>

            <div className="right-bx">
              <div>
                <span>Prescription ID</span>
                <p>{`${data?.order.orderForm?.campaign?.orderNumberSequencePrefix || ""} ${data?.order.orderForm.eyeCheckingNumber}`}</p>
              </div>
              {data?.order.orderForm?.campaign ? (
                <>
                  <div>
                    <span>Campaign ID</span>
                    <p>
                      {
                        data?.order.orderForm?.campaign
                          ?.orderNumberSequencePrefix
                      }
                    </p>
                  </div>

                  <div>
                    <span>Campaign Location</span>
                    <p>{data?.order.orderForm?.campaign?.place}</p>
                  </div>
                </>
              ) : null}

              <div>
                <span>Last Updated At</span>
                <p>
                  <small>Date:</small> {dateFormatter(data?.order.updatedAt)}
                </p>
              </div>

              <div>
                <span>Order Status</span>
                <div>
                  <Chip
                    text={data?.order.status}
                    variant={getOrderStatusChipColor(data?.order.status)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="payments-list">
            <div className="table-title">
              <h3>Payment Details</h3>
            </div>

            <div className="table-content">
              <table>
                <thead>
                  <tr>
                    <th>Payment Type</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Date</th>
                  </tr>
                </thead>

                <tbody>
                  {data?.order.payments.map((payment) => (
                    <tr key={payment._id}>
                      <td>{payment.type}</td>
                      <td>{currencyFormatter(payment.amount)} LKR</td>
                      <td>{payment.method}</td>
                      <td>{dateFormatter(payment.date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Section>
      </div>
      <OrderUpdateModal
        showStatusModal={showStatusModal}
        setShowStatusModal={setShowStatusModal}
        remainingBalance={getRemainingAmount()}
        currentOrderStatus={data?.order.status}
        isTwoPayment={
          data?.order.payments[0].type === enums.PAYMENT_TYPES.ADVANCE
        }
        orderId={orderId}
      />
      <PrescriptionModal
        showPrescriptionModal={showPrescriptionModal}
        setShowPrescriptionModal={setShowPrescriptionModal}
        prescriptionId={data?.order.orderForm?._id}
      />
    </>
  );
}

function OrderUpdateModal({
  showStatusModal = false,
  setShowStatusModal,
  remainingBalance = 0,
  currentOrderStatus = enums.ORDER_STATUSES.CONFIRMED,
  isTwoPayment = false,
  orderId,
}) {
  const queryClient = useQueryClient();
  const { updateOrder, queryKey } = useOrderService();

  const updateOrderMutation = useMutation({
    mutationFn: updateOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success("Order status updated successfully");
      setShowStatusModal(false);
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.payload?.message || "Failed to update order",
      );
    },
  });

  const orderStatuses = useMemo(() => {
    return Object.values(enums.ORDER_STATUSES)
      .filter((status) => status !== enums.ORDER_STATUSES.ANY)
      .map((status) => ({
        label: status,
        value: status,
        disabled: status === currentOrderStatus,
      }));
  }, [currentOrderStatus]);

  const paymentMethods = useMemo(() => {
    return Object.values(enums.PAYMENT_METHODS)
      .filter((method) => method !== enums.PAYMENT_METHODS.CHEQUE)
      .map((method) => ({
        label: method,
        value: method,
      }));
  }, []);

  const handleSubmit = (values) => {
    const payload = {};
    if (values.status) payload.status = values.status;

    if (
      values.status === enums.ORDER_STATUSES.COMPLETED &&
      remainingBalance > 0
    ) {
      if (!values.balancePayMethod) {
        toast.error("Please select a payment method for the balance.");
        return;
      }
      payload.newPayments = [
        {
          amount: remainingBalance,
          type: enums.PAYMENT_TYPES.BALANCE,
          method: values.balancePayMethod,
          date: new Date(),
        },
      ];
    }

    updateOrderMutation.mutate({ orderId, payload });
  };

  return (
    <Modal
      title="Update Order Status"
      containerClassName="update-order-status-modal"
      close={!showStatusModal}
      onClose={() => setShowStatusModal((prev) => !prev)}
    >
      <Section>
        <Formik
          initialValues={{
            status: currentOrderStatus,
            balancePayMethod: "",
          }}
          validationSchema={orderUpdateSchema}
          onSubmit={handleSubmit}
        >
          {({ values, isSubmitting }) => (
            <Form>
              <div className="d-row">
                <div>
                  <span>Payment Type</span>
                  <p>{isTwoPayment ? "Two Payment" : "Single Payment"}</p>
                </div>

                <div>
                  <span>Balance Amount</span>
                  <p>{currencyFormatter(remainingBalance)} LKR</p>
                </div>
              </div>
              <SelectInput
                label="Order status"
                name="status"
                values={orderStatuses}
              />
              {values.status === enums.ORDER_STATUSES.COMPLETED &&
                remainingBalance > 0 && (
                  <SelectInput
                    label="Balance Payment Method"
                    name="balancePayMethod"
                    values={[
                      { label: "Select a payment method", value: "" },
                      ...paymentMethods,
                    ]}
                  />
                )}
              <Button
                text={
                  updateOrderMutation.isPending
                    ? "Updating..."
                    : "Update Order Status"
                }
                type="submit"
                icon={<EditIcon />}
                disabled={updateOrderMutation.isPending}
              />
            </Form>
          )}
        </Formik>
      </Section>
    </Modal>
  );
}

function PrescriptionModal({
  showPrescriptionModal,
  setShowPrescriptionModal,
  prescriptionId,
}) {
  const { getOrderFormById, queryKey } = useOrderFormService();
  const { data, isLoading, error } = useQuery({
    queryKey: [queryKey, prescriptionId],
    queryFn: () => getOrderFormById(prescriptionId),
    enabled: !!prescriptionId,
  });

  if (isLoading) {
    return <Loading variant="fullScreen" />;
  }

  if (error) {
    toast.error(error.message || enums.DEFAULT_ERROR_MESSAGE);
  }

  const initialValues = {
    eyeCheckingNumber: data?.orderForm?.eyeCheckingNumber,
    CR39: data?.orderForm?.CR39 ? data?.orderForm?.CR39 : "",
    PHOTO_GREY: data?.orderForm?.PHOTO_GREY ? data?.orderForm?.PHOTO_GREY : "",
    MULTI_COATED: data?.orderForm?.MULTI_COATED
      ? data?.orderForm?.MULTI_COATED
      : "",
    PROGRESSIVE: data?.orderForm?.PROGRESSIVE
      ? data?.orderForm?.PROGRESSIVE
      : "",
    EASY_FIT: data?.orderForm?.EASY_FIT ? data?.orderForm?.EASY_FIT : "",
    hb: data?.orderForm?.hb || "",
    distance: {
      right: {
        sph: data?.orderForm?.distance?.right?.sph || null,
        cyl: data?.orderForm?.distance?.right?.cyl || null,
        axis: data?.orderForm?.distance?.right?.axis || 0,
      },
      left: {
        sph: data?.orderForm?.distance?.left?.sph || null,
        cyl: data?.orderForm?.distance?.left?.cyl || null,
        axis: data?.orderForm?.distance?.left?.axis || 0,
      },
    },
    near: {
      right: {
        sph: data?.orderForm?.near?.right?.sph || null,
        cyl: data?.orderForm?.near?.right?.cyl || null,
        axis: data?.orderForm?.near?.right?.axis || 0,
      },
      left: {
        sph: data?.orderForm?.near?.left?.sph || null,
        cyl: data?.orderForm?.near?.left?.cyl || null,
        axis: data?.orderForm?.near?.left?.axis || 0,
      },
    },
    frame: null,
    lense: null,
    discount: 0,
    orderSchedule: "now",
    paymentType: "fullPayment",
    paymentMethod: enums.PAYMENT_METHODS.CASH,
    // UI helper fields
    frameCode: "",
    rightLensCode: "",
    leftLensCode: "",
  };
  return (
    <Modal
      className="prescription-modal"
      title="Prescription"
      close={!showPrescriptionModal}
      onClose={() => setShowPrescriptionModal(false)}
    >
      <Formik initialValues={initialValues} enableReinitialize>
        <Form>
          <Section
            title="Prescription Details"
            className="prescription-details"
          >
            <TextInput
              className="prescription-number-input"
              name="eyeCheckingNumber"
              disabled
              label="Prescription Number"
            />

            <div className="meta-box">
              <div className="date">
                <span>Date</span>
                <p>{dateFormatter(data?.orderForm?.testDate)}</p>
              </div>

              {/* <div className="created">
                  <span>Created by</span>
                  <p>
                    <span className="role">Admin</span> Mr. Kalpa Kavindu
                  </p>
                </div> */}
            </div>
          </Section>

          <Section
            title="Eye Measurements"
            className="eye-measurements-section"
          >
            <div className="input-group">
              <TextInput
                label="Half Bifocal (H.B)"
                placeholder="Type here"
                name="hb"
                prefix="+"
                disabled
              />

              <div className="lens-options">
                <span>Lens options</span>

                <div>
                  <div className="checkbox-group">
                    <Checkbox name="CR39" disabled label="CR39" />
                    <Checkbox name="PHOTO_GREY" disabled label="Photo Grey" />
                    <Checkbox
                      name="MULTI_COATED"
                      disabled
                      label="Multi Coated"
                    />
                  </div>

                  <div className="checkbox-group">
                    <Checkbox name="PROGRESSIVE" disabled label="Progressive" />
                    <Checkbox name="EASY_FIT" disabled label="Easy Fit" />
                  </div>
                </div>
              </div>
            </div>

            <div className="table-group">
              <div className="table-content">
                <table>
                  <thead>
                    <tr>
                      <th>R</th>
                      <th>SPH</th>
                      <th>CYL</th>
                      <th>AXIS</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td>DIST</td>
                      <td>
                        <EyeMeasurementInput
                          disabled
                          name="distance.right.sph"
                        />
                      </td>
                      <td>
                        <EyeMeasurementInput
                          disabled
                          name="distance.right.cyl"
                        />
                      </td>
                      <td>
                        <TextInput
                          className="tinput"
                          disabled
                          name="distance.right.axis"
                        />
                      </td>
                    </tr>

                    <tr>
                      <td>NEAR</td>
                      <td>
                        <EyeMeasurementInput disabled name="near.right.sph" />
                      </td>
                      <td>
                        <EyeMeasurementInput disabled name="near.right.cyl" />
                      </td>
                      <td>
                        <TextInput
                          className="tinput"
                          disabled
                          name="near.right.axis"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="table-content">
                <table>
                  <thead>
                    <tr>
                      <th>L</th>
                      <th>SPH</th>
                      <th>CYL</th>
                      <th>AXIS</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td>DIST</td>
                      <td>
                        <EyeMeasurementInput
                          disabled
                          name="distance.left.sph"
                        />
                      </td>
                      <td>
                        <EyeMeasurementInput
                          disabled
                          name="distance.left.cyl"
                        />
                      </td>
                      <td>
                        <TextInput
                          className="tinput"
                          disabled
                          name="distance.left.axis"
                        />
                      </td>
                    </tr>

                    <tr>
                      <td>NEAR</td>
                      <td>
                        <EyeMeasurementInput disabled name="near.left.sph" />
                      </td>
                      <td>
                        <EyeMeasurementInput disabled name="near.left.cyl" />
                      </td>
                      <td>
                        <TextInput
                          className="tinput"
                          disabled
                          name="near.left.axis"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Section>

          <Section title="Lens Details" className="lens-details-section">
            <div className="right-lens">
              <h3>Right Lens</h3>
              <div className="lens-table">
                <div className="table-container">
                  <table>
                    <tbody>
                      <tr>
                        <td
                          rowSpan={
                            data?.orderForm?.lense?.right?.selectedLense
                              ?.attributes?.coating ===
                            enums.LENSE_COATINGS.PHOTO_GREY
                              ? 7
                              : 6
                          }
                          className="table-title"
                        >
                          <p>LENS</p>
                        </td>
                        <td className="table-sub-title">Brand</td>
                        <td>
                          {data?.orderForm?.lense?.right?.selectedLense
                            ?.brand || " - "}
                        </td>
                      </tr>

                      <tr>
                        <td className="table-sub-title">Type</td>
                        <td>
                          {data?.orderForm?.lense?.right?.selectedLense
                            ?.attributes?.lense_type || " - "}
                        </td>
                      </tr>
                      <tr>
                        <td className="table-sub-title">Color</td>
                        <td>
                          {data?.orderForm?.lense?.right?.selectedLense
                            ?.attributes?.coating || " - "}
                        </td>
                      </tr>

                      {data?.orderForm?.lense?.right?.selectedLense?.attributes
                        ?.coating === enums.LENSE_COATINGS.PHOTO_GREY ? (
                        <tr>
                          <td className="table-sub-title">Transition</td>
                          <td>
                            {data?.orderForm?.lense?.right?.selectedLense
                              ?.attributes?.transition || " - "}
                          </td>
                        </tr>
                      ) : null}
                      <tr>
                        <td className="table-sub-title">SEG</td>
                        <td>
                          24 x{" "}
                          {data?.orderForm?.lense?.right?.selectedLense
                            ?.attributes?.seg
                            ? `${data?.orderForm?.lense?.right?.selectedLense?.attributes?.seg} mm`
                            : "  "}
                        </td>
                      </tr>
                      <tr>
                        <td className="table-sub-title">Diameter</td>
                        <td>
                          {data?.orderForm?.lense?.right?.diameter
                            ? `${data?.orderForm?.lense?.right?.diameter} mm`
                            : " - "}
                        </td>
                      </tr>
                      <tr>
                        <td className="table-sub-title">Price</td>
                        <td>
                          {currencyFormatter(
                            parseInt(
                              data?.orderForm?.lense?.right?.selectedLense
                                ?.sellingPrice || 0,
                            ),
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="separator"></div>

            <div className="left-lens">
              <h3>Left Lens</h3>
              <div className="lens-table">
                <div className="table-container">
                  <table>
                    <tbody>
                      <tr>
                        <td
                          rowSpan={
                            data?.orderForm?.lense?.left?.selectedLense
                              ?.attributes?.coating ===
                            enums.LENSE_COATINGS.PHOTO_GREY
                              ? 7
                              : 6
                          }
                          className="table-title"
                        >
                          <p>LENS</p>
                        </td>
                        <td className="table-sub-title">Brand</td>
                        <td>
                          {data?.orderForm?.lense?.left?.selectedLense?.brand ||
                            " - "}
                        </td>
                      </tr>

                      <tr>
                        <td className="table-sub-title">Type</td>
                        <td>
                          {data?.orderForm?.lense?.left?.selectedLense
                            ?.attributes?.lense_type || " - "}
                        </td>
                      </tr>
                      <tr>
                        <td className="table-sub-title">Color</td>
                        <td>
                          {data?.orderForm?.lense?.left?.selectedLense
                            ?.attributes?.coating || " - "}
                        </td>
                      </tr>

                      {data?.orderForm?.lense?.left?.selectedLense?.attributes
                        ?.coating === enums.LENSE_COATINGS.PHOTO_GREY ? (
                        <tr>
                          <td className="table-sub-title">Transition</td>
                          <td>
                            {data?.orderForm?.lense?.left?.selectedLense
                              ?.attributes?.transition || " - "}
                          </td>
                        </tr>
                      ) : null}
                      <tr>
                        <td className="table-sub-title">SEG</td>
                        <td>
                          24 x{" "}
                          {data?.orderForm?.lense?.left?.selectedLense
                            ?.attributes?.seg
                            ? `${data?.orderForm?.lense?.left?.selectedLense?.attributes?.seg} mm`
                            : "  "}
                        </td>
                      </tr>
                      <tr>
                        <td className="table-sub-title">Diameter</td>
                        <td>
                          {data?.orderForm?.lense?.left?.diameter
                            ? `${data?.orderForm?.lense?.left?.diameter} mm`
                            : " - "}
                        </td>
                      </tr>
                      <tr>
                        <td className="table-sub-title">Price</td>
                        <td>
                          {currencyFormatter(
                            parseInt(
                              data?.orderForm?.lense?.left?.selectedLense
                                ?.sellingPrice || 0,
                            ),
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Section>

          <Section title="Frame Details" className="frame-details-section">
            <div className="frame-table">
              <div className="table-container">
                <table>
                  <tr>
                    <td rowSpan={8} className="table-title">
                      <p>FRAME</p>
                    </td>
                    <td className="table-sub-title">Modal</td>
                    <td colSpan={2}>
                      {
                        data?.orderForm?.frame?.selectedFrame?.attributes
                          ?.model_number
                      }
                    </td>
                  </tr>

                  <tr>
                    <td rowSpan={3} className="table-sub-title">
                      Size
                    </td>
                    <td className="table-sub-sub-title">Lens diagonal</td>
                    <td>
                      {
                        data?.orderForm?.frame?.selectedFrame?.attributes?.size
                          ?.lens_diagonal
                      }
                    </td>
                  </tr>

                  <tr>
                    <td className="table-sub-sub-title">Bridge size</td>
                    <td>
                      {
                        data?.orderForm?.frame?.selectedFrame?.attributes?.size
                          ?.bridge
                      }
                    </td>
                  </tr>

                  <tr>
                    <td className="table-sub-sub-title">Arm Length</td>
                    <td>
                      {
                        data?.orderForm?.frame?.selectedFrame?.attributes?.size
                          ?.arm_length
                      }
                    </td>
                  </tr>

                  <tr>
                    <td className="table-sub-title">Color</td>
                    <td colSpan={2}>
                      {data?.orderForm?.frame?.selectedFrame?.attributes?.color}
                    </td>
                  </tr>

                  <tr>
                    <td className="table-sub-title">Material</td>
                    <td colSpan={2}>
                      {
                        data?.orderForm?.frame?.selectedFrame?.attributes
                          ?.material
                      }
                    </td>
                  </tr>

                  <tr>
                    <td className="table-sub-title">P.D</td>
                    <td colSpan={2}>{data?.orderForm?.frame?.pd}</td>
                  </tr>
                  <tr>
                    <td className="table-sub-title">Price</td>
                    <td colSpan={2}>
                      {currencyFormatter(
                        parseInt(
                          data?.orderForm?.frame?.selectedFrame?.sellingPrice ||
                            0,
                        ),
                      )}
                    </td>
                  </tr>
                </table>
              </div>
            </div>
          </Section>
        </Form>
      </Formik>
    </Modal>
  );
}

export default OrderDetailsPage;
